import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Checkbox, FormControlLabel, Tooltip } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import {
  ButtonTabs,
  Chip,
  ConfirmDeleteModal,
  Icon,
  IconButton,
  type SemanticIconName,
  Typography,
} from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { type EnrichedChunk } from '~types'

import { chunkTypeIconMap } from '../../../constants/chunkTypeIcons'

import './ChunkCard.scss'

const FIGURE_VIEW_TABS = [
  { label: 'Image', value: 'image' },
  { label: 'Description', value: 'text' },
] as const

export interface ChunkCardProps {
  chunk: EnrichedChunk
  index: number
  onCopy: (text: string, id: string) => Promise<void>
  copiedId: string | null
  organizationIdentityId: string
  onDeleted?: (chunkId: string) => void
}

const ChunkCard: React.FC<ChunkCardProps> = ({
  chunk,
  index,
  onCopy,
  copiedId,
  organizationIdentityId,
  onDeleted,
}) => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const isSuper = useSelector((state: RootState) => state?.auth?.user?.super)
  const hasMetadata = chunk.dates.length > 0 || chunk.urls.length > 0
  const isFigure = chunk.chunk_type === 'Figure'
  const [figureView, setFigureView] = useState<'image' | 'text'>('image')
  const [showInChat, setShowInChat] = useState<boolean>(chunk.show_in_chat ?? false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)

  const imageUrl = isFigure ? (chunk.image_url ?? null) : null
  const chunkTypeIcon = chunk.chunk_type
    ? (chunkTypeIconMap[chunk.chunk_type] ?? chunkTypeIconMap['Unknown'])
    : null

  const handleShowInChatChange = async (checked: boolean): Promise<void> => {
    if (!userId) return
    setShowInChat(checked)
    try {
      await apiZodios.ai_editChunk({
        user_id: userId,
        organization_identity_id: organizationIdentityId,
        chunk_id: chunk.chunk_id,
        show_in_chat: checked,
      })
    } catch (err) {
      console.error('Failed to update show_in_chat:', err)
      setShowInChat(!checked)
    }
  }

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!userId) return
    setDeleteDialogOpen(false)
    try {
      await apiZodios.ai_deleteChunk({
        user_id: userId,
        organization_identity_id: organizationIdentityId,
        chunk_id: chunk.chunk_id,
      })
      onDeleted?.(chunk.chunk_id)
    } catch (err) {
      console.error('Failed to delete chunk:', err)
    }
  }

  return (
    <div className='chunk-card'>
      <div className='chunk-card__header'>
        <div className='chunk-card__header-meta'>
          <Typography variant='subtitle2' className='chunk-card__index'>
            Chunk {index + 1}
          </Typography>

          {chunk.chunk_type && chunkTypeIcon && (
            <Chip
              color='neutral'
              icon={<Icon name={chunkTypeIcon as SemanticIconName} />}
              label={chunk.chunk_type}
            />
          )}

          {isFigure && (
            <ButtonTabs
              options={[...FIGURE_VIEW_TABS]}
              value={figureView}
              onChange={(value) => setFigureView(value as 'image' | 'text')}
            />
          )}
        </div>

        <div className='chunk-card__header-actions'>
          {isFigure && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showInChat}
                  onChange={(e) => handleShowInChatChange(e.target.checked)}
                  size='small'
                />
              }
              label={
                <Typography variant='caption' color='textSecondary'>
                  Show in chat
                </Typography>
              }
              className='chunk-card__show-in-chat'
            />
          )}

          <Tooltip title='Copy chunk text'>
            <IconButton
              size='small'
              onClick={() => onCopy(chunk.text, `chunk-text-${chunk.chunk_id}`)}
            >
              <Icon name='copy' fontSize='small' />
            </IconButton>
          </Tooltip>
          {copiedId === `chunk-text-${chunk.chunk_id}` && (
            <Typography variant='caption' color='success.main'>
              Copied!
            </Typography>
          )}

          {isSuper && (
            <IconButton size='small' color='error' onClick={() => setDeleteDialogOpen(true)}>
              <Icon name='delete' fontSize='small' />
            </IconButton>
          )}
        </div>
      </div>

      {isFigure && figureView === 'image' && (
        <div className='chunk-card__figure'>
          {imageUrl ? (
            <img src={imageUrl} alt='Figure' className='chunk-card__figure-image' />
          ) : (
            <Typography variant='caption' color='textSecondary'>
              No image URL available
            </Typography>
          )}
        </div>
      )}

      {(!isFigure || figureView === 'text') && <pre className='chunk-card__text'>{chunk.text}</pre>}

      {hasMetadata && (
        <div className='chunk-card__metadata'>
          {chunk.dates.length > 0 && (
            <div className='chunk-card__metadata-section'>
              <div className='chunk-card__metadata-label'>
                <Icon name='calendar' fontSize='small' color='inherit' />
                <Typography
                  variant='caption'
                  color='primary'
                  className='chunk-card__metadata-title'
                >
                  Dates ({chunk.dates.length})
                </Typography>
              </div>
              <div className='chunk-card__metadata-chips'>
                {chunk.dates.map((date, idx) => (
                  <Chip key={idx} label={date} size='small' variant='outlined' />
                ))}
              </div>
            </div>
          )}

          {chunk.urls.length > 0 && (
            <div className='chunk-card__metadata-section'>
              <div className='chunk-card__metadata-label'>
                <Icon name='link' fontSize='small' color='inherit' />
                <Typography
                  variant='caption'
                  color='secondary'
                  className='chunk-card__metadata-title'
                >
                  Links ({chunk.urls.length})
                </Typography>
              </div>
              <div className='chunk-card__metadata-links'>
                {chunk.urls.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='chunk-card__metadata-link'
                  >
                    {url}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title='Delete chunk?'
        description='This cannot be undone.'
      />
    </div>
  )
}

export default ChunkCard
